import { ReactNode } from "react";
import { LucideIcon } from "lucide-react";

/**
 * Reusable retro/comic empty-state shell used across the member portal
 * (Saved Episodes, Tracked Topics, Playlists, Notes).
 *
 * Mobile-first design:
 *  - Outer wrapper uses `overflow-hidden` + `mx-1` to keep rotated card +
 *    chunky shadow from triggering horizontal page scroll on narrow phones.
 *  - Sparkle badge positioned with `top-2 right-2` (inside) on mobile, then
 *    bumps to negative offsets on `sm:` so we never clip on small screens.
 *  - Padding scales: tighter on phone, roomier on tablet+.
 */
interface EmptyStateShellProps {
  icon: LucideIcon;
  emoji?: string;
  headline: string;
  blurb: string;
  children?: ReactNode;
  /** Slight rotation in degrees for the comic-card vibe. Default -1.2. */
  rotation?: number;
}

export default function EmptyStateShell({
  icon: Icon,
  emoji,
  headline,
  blurb,
  children,
  rotation = -1.2,
}: EmptyStateShellProps) {
  return (
    // Outer guard: clip any rotation/shadow overflow so the page never
    // gains a horizontal scrollbar on small phones.
    <div className="overflow-hidden px-1 sm:px-0 py-5 sm:py-8">
      <div
        className="relative max-w-xl mx-auto retro-border bg-card p-4 sm:p-7"
        style={{
          transform: `rotate(${rotation}deg)`,
          boxShadow: "5px 5px 0 hsl(var(--foreground))",
        }}
      >
        {/* Sparkle / corner badge — sits inside the card on mobile so it
            never clips, then pops outside on sm+. */}
        <div
          className="absolute top-2 right-2 sm:-top-3 sm:-right-3 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-retro-amber border-[3px] border-foreground flex items-center justify-center text-sm sm:text-base font-retro font-bold shadow-[2px_2px_0_hsl(var(--foreground))]"
          aria-hidden
        >
          {emoji ?? "✨"}
        </div>

        {/* Headline row — leave room on the right for the sparkle badge so
            the title can never collide with it on mobile. */}
        <div className="flex items-center gap-2.5 mb-2 pr-10 sm:pr-0">
          <div className="w-9 h-9 sm:w-10 sm:h-10 retro-border bg-retro-amber/30 flex items-center justify-center flex-shrink-0">
            <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-foreground" strokeWidth={2.5} />
          </div>
          <h2
            className="font-retro font-bold text-base sm:text-xl leading-tight"
            style={{ transform: "rotate(0.4deg)" }}
          >
            {headline}
          </h2>
        </div>

        {/* Blurb */}
        <p className="font-mono text-[11px] sm:text-sm text-muted-foreground leading-relaxed mb-3 sm:mb-4">
          {blurb}
        </p>

        {/* Slot for CTA + dynamic content */}
        {children && <div className="mt-3">{children}</div>}
      </div>
    </div>
  );
}
